/*
 * tcs-variant-availability.js — sold-out options are blocked except on PDPs
 * with WC restock explicitly enabled, where selection is required to request an alert.
 *
 * WHY THIS EXISTS. Horizon renders an unavailable variant option as a fully
 * working radio that merely *announces* itself as disabled:
 * snippets/variant-main-picker.liquid emits `aria-disabled="true"` and NO
 * `disabled` attribute, and assets/variant-picker.js binds `change` on the
 * component and calls variantChanged() for any radio, available or not. That
 * is deliberate upstream behaviour — it lets a shopper open a sold-out shade
 * and see that it is sold out. Measured on the QA theme: clicking a sold-out
 * option selected it, moved `?variant=` into the URL and flipped the buy
 * button to SOLD OUT, and ArrowRight did the same through native radio-group
 * roving selection.
 *
 * Daria ruled on 2026-09-09 that a sold-out option must not become the
 * selected variant. Both vendored files above are hash-enforced and NOT on the
 * Horizon owned list, so editing either would need a CTO ruling and permanent
 * upgrade debt. This file is the TCS-owned route instead: it needs no ruling
 * and no vendored change, and it is loaded from snippets/tcs-head.liquid on
 * the product template only.
 *
 * On 2026-09-10, Daria approved explicit sold-out restock subscriptions.
 * PDPs explicitly enabling restock now permit sold-out selection; the WC
 * adapter removes aria-disabled there and purchase availability stays with
 * Horizon. The contract below still applies to PDPs without that block.
 *
 * FOCUS CONTRACT. No native disabled attribute is added. Horizon's accessible
 * name and aria-disabled remain, but a radio group normally has only its
 * checked member in the Tab order. Arrows skip unavailable values; they are
 * not all independently keyboard-focusable. A server-checked unavailable
 * value remains the group's focus entry, and Tab/Shift+Tab can exit.
 *
 * GENERIC BY CONSTRUCTION. The only signal read is `data-option-available`,
 * which Horizon emits per option value from Shopify's own
 * `product_option_value.available`: Shopify's TOP-DOWN option availability,
 * respecting previous option selections and any purchasable subsequent
 * combination. This is not the fully selected variant's availability. Nothing here knows a product
 * handle, an option name, or how many dimensions exist. A multi-dimension
 * product renders one fieldset per dimension and each radio group is scoped by
 * its `name` attribute, so the arrow-skip below walks only the dimension the
 * shopper is actually in.
 *
 * SURVIVES RE-RENDER. Horizon morphs the product section on every variant
 * change, which replaces these inputs. Both listeners are delegated from
 * `document` in the capture phase, so there is nothing to re-bind and no
 * listener gap after replacement. Flags still describe the latest rendered
 * response, not inventory fetched independently by this guard.
 *
 * SERVER SELECTION IS NOT RECONCILED HERE. An all-sold-out product can arrive
 * with an unavailable value checked. In mixed Color/Size inventory, changing
 * an available color can also make the retained size unavailable; Shopify may
 * render that unavailable size checked. This guard blocks activation of values
 * flagged unavailable in the current DOM, not every sold-out full combination.
 * It does not silently substitute a shopper's size, and cannot predict flags
 * while a section request is pending. Horizon remains responsible for request
 * cancellation, the resolved variant and the buy button's availability.
 * The TCS CSS keeps any server-checked unavailable value muted, never raspberry.
 * The mixed fixture documents this boundary; it is not inventory/network QA.
 */
(function () {
  'use strict';

  var LABEL = '.variant-option__button-label';
  var PRODUCT_MAIN = '#MainContent[data-template^="product"]';

  /**
   * The option label the event belongs to, but only inside the PDP's own
   * variant picker. Product cards and quick-add dialogs render the same
   * classes and are deliberately left alone — the same exclusions Horizon's
   * variant-picker.js makes for its own on-product-page branch.
   * @param {EventTarget|null} target
   * @returns {HTMLElement|null}
   */
  function optionLabel(target) {
    if (!target || typeof (/** @type {Element} */ (target).closest) !== 'function') return null;
    var label = /** @type {Element} */ (target).closest(LABEL);
    if (!label) return null;
    if (!label.closest('variant-picker')) return null;
    var main = label.closest(PRODUCT_MAIN);
    if (!main) return null;
    // Daria's 2026-09-10 wishlist/restock decision permits selecting a sold-out
    // shade for notification. Horizon still disables its purchase button.
    if (main.querySelector('tcs-product-actions[data-restock-enabled]')) return null;
    if (label.closest('product-card') || label.closest('quick-add-dialog')) return null;
    return /** @type {HTMLElement} */ (label);
  }

  /** @param {Element} label @returns {HTMLInputElement|null} */
  function radioOf(label) {
    return /** @type {HTMLInputElement|null} */ (label.querySelector('input[type="radio"]'));
  }

  /** @param {HTMLInputElement|null} radio @returns {boolean} */
  function isSoldOut(radio) {
    return !!radio && radio.dataset.optionAvailable === 'false';
  }

  /* ── Pointer ──────────────────────────────────────────────────────────
     preventDefault on a radio's click cancels its activation behaviour, so
     the option never becomes checked and Horizon's `change` handler never
     fires. Capture phase, so this lands before the component sees it. */
  document.addEventListener(
    'click',
    function (event) {
      var label = optionLabel(event.target);
      if (!label || !isSoldOut(radioOf(label))) return;
      event.preventDefault();
      event.stopPropagation();
    },
    true,
  );

  var STEP = { ArrowRight: 1, ArrowDown: 1, ArrowLeft: -1, ArrowUp: -1 };

  /* ── Keyboard ─────────────────────────────────────────────────────────
     Native radio groups move focus AND check in one keypress, and they
     already skip `disabled` radios. We are not using `disabled`, so the skip
     is reimplemented here over `data-option-available` instead: arrows land
     on the next AVAILABLE option in the pressed direction, wrapping, exactly
     as the native behaviour would. Space and Enter cannot activate a sold-out
     option either.

     No focus trap: only arrow keys are intercepted, so Tab and Shift+Tab
     always leave the group. When a dimension has no available option at all
     the arrow is swallowed and focus simply stays put — there is nowhere
     legitimate to go, and Tab still works. */
  document.addEventListener(
    'keydown',
    function (event) {
      var label = optionLabel(event.target);
      if (!label) return;
      var current = radioOf(label);
      if (!current) return;

      if (event.key === ' ' || event.key === 'Spacebar' || event.key === 'Enter') {
        if (isSoldOut(current)) {
          event.preventDefault();
          event.stopPropagation();
        }
        return;
      }

      var step = STEP[event.key];
      if (!step) return;

      var fieldset = label.closest('fieldset');
      if (!fieldset) return;

      /* The radio group is the set sharing this input's name — that is what
         scopes the walk to one option dimension on a multi-option product. */
      var all = fieldset.querySelectorAll('input[type="radio"]');
      var group = [];
      for (var i = 0; i < all.length; i++) {
        if (/** @type {HTMLInputElement} */ (all[i]).name === current.name) group.push(all[i]);
      }
      if (group.length < 2) return;

      var from = group.indexOf(current);
      if (from === -1) return;

      var next = null;
      for (var n = 1; n < group.length; n++) {
        var at = (((from + step * n) % group.length) + group.length) % group.length;
        if (!isSoldOut(/** @type {HTMLInputElement} */ (group[at]))) {
          next = group[at];
          break;
        }
      }

      event.preventDefault();
      event.stopPropagation();
      if (!next) return;

      /* .click() rather than setting .checked: it fires the same activation
         and `change` Horizon listens for, so media, price, URL and the buy
         button update exactly as they do for a mouse selection. */
      next.focus();
      next.click();
    },
    true,
  );
})();
